import React from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CompareProvider } from './context/CompareContext';
import AuthCallback from './components/AuthCallback';
import Home from './pages/Home';
import Search from './pages/Search';
import PropertyDetail from './pages/PropertyDetail';
import Login from './pages/Login';
import RoleSelect from './pages/RoleSelect';
import MobileVerify from './pages/MobileVerify';
import Dashboard from './pages/Dashboard';
import AddProperty from './pages/AddProperty';
import AdminPanel from './pages/AdminPanel';
import BoostPlans from './pages/BoostPlans';
import ComparePage from './pages/Compare';
import Messages from './pages/Messages';
import PostRequirement from './pages/PostRequirement';
import MobileBottomNav from './components/MobileBottomNav';
import InstallPromptBanner from './components/InstallPromptBanner';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import CompareTray from './components/CompareTray';
import { Toaster } from './components/ui/sonner';
import './App.css';
import { getSessionId } from './utils/track';

const OWNER_ROLES = ['owner', 'broker', 'admin'];
const ADMIN_ROLES = ['admin'];

// Fire page_view tracking once per browser session
(function trackOnce() {
  try {
    fetch('/api/track', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_id: getSessionId(),
        event: 'page_view',
        page: window.location.pathname,
        metadata: {},
      }),
    }).catch(() => {});
  } catch (_) { console.error('Initial page_view tracking failed:', _); }
}());

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-700"></div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) return <AuthCallback />;
  return (
    <>
      <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/search" element={<Search />} />
          <Route path="/property/:id" element={<PropertyDetail />} />
          <Route path="/login" element={<Login />} />
          <Route path="/role-select" element={<RoleSelect />} />
          <Route path="/verify-mobile" element={<ProtectedRoute><MobileVerify /></ProtectedRoute>} />
          <Route path="/plans" element={<BoostPlans />} />
          <Route path="/compare" element={<ComparePage />} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
          <Route path="/post-requirement" element={<PostRequirement />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/add-property" element={<ProtectedRoute roles={OWNER_ROLES}><AddProperty /></ProtectedRoute>} />
          <Route path="/edit-property/:id" element={<ProtectedRoute roles={OWNER_ROLES}><AddProperty /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute roles={ADMIN_ROLES}><AdminPanel /></ProtectedRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <CompareTray />
        <MobileBottomNav />
        <InstallPromptBanner />
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <CompareProvider>
          <BrowserRouter>
            <AppRouter />
            <Toaster position="top-right" richColors />
          </BrowserRouter>
        </CompareProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
